import React, {useEffect, useMemo, useState} from "react";
import {observer} from "mobx-react-lite";
import {useNavigate, useParams} from "react-router-dom";
import PageLayout from "../PageLayout";
import Btn from "../../ui/Btn";
import appStore from "../../storage/AppStore";
import clubStore from "../../storage/ClubStore";
import systemStore from "../../storage/SystemStore";
import "../../common/game.css";
import Select from "../../ui/Select";

const safeArray = (v) => Array.isArray(v) ? v : [];

const getTournamentTeamGameTeamId = (item) =>
  item?.game_team_id || item?.game_team?.id || item?.id || null;

const getTournamentTeamName = (item) =>
  item?.game_team?.name || item?.name || "-";

const getTournamentTeamTournamentTeamId = (item) =>
  item?.tournament_team_id || item?.id || null;

const getMembersCount = (item) => {
  if (typeof item?.members_count === "number") return item.members_count;
  if (typeof item?.game_team?.members_count === "number") return item.game_team.members_count;
  if (Array.isArray(item?.members)) return item.members.length;
  if (Array.isArray(item?.game_team?.members)) return item.game_team.members.length;
  return 0;
};

const getGamesCount = (item) => {
  if (typeof item?.games_count === "number") return item.games_count;
  if (typeof item?.game_team?.games_count === "number") return item.game_team.games_count;
  if (Array.isArray(item?.games)) return item.games.length;
  if (Array.isArray(item?.game_team?.games)) return item.game_team.games.length;
  return 0;
};

const getTournamentsCount = (item) => {
  if (typeof item?.tournaments_count === "number") return item.tournaments_count;
  if (typeof item?.game_team?.tournaments_count === "number") return item.game_team.tournaments_count;
  if (Array.isArray(item?.tournaments)) return item.tournaments.length;
  if (Array.isArray(item?.game_team?.tournaments)) return item.game_team.tournaments.length;
  return 0;
};

const getDivisionName = (item) =>
  item?.division?.name || appStore.tr.no_division || "No Category";

const getGroupName = (item) =>
  item?.group?.name || appStore.tr.no_group || "No Group";

const CompetitionTeams = ({role = "MGR"}) => {
  const navigate = useNavigate();
  const {competition_id} = useParams();

  const basePath = role === "TRN" ? "/trainer" : "/manager";

  const [availableTeams, setAvailableTeams] = useState([]);
  const [selectedGameTeamId, setSelectedGameTeamId] = useState(null);
  const [loadingAdd, setLoadingAdd] = useState(false);
  const [loadingRemoveId, setLoadingRemoveId] = useState(null);
  const [savingDivisionByTournamentTeamId, setSavingDivisionByTournamentTeamId] = useState({});

  const myCompanyId = clubStore.club?.id;

  useEffect(() => {
    if (!competition_id || !myCompanyId) return;

    clubStore.tournament.getCompetition(competition_id).catch(console.error);
    clubStore.tournament.getTournamentDivisions(competition_id).catch(console.error);

    clubStore.tournament.getTeamsForCompany(myCompanyId)
      .then((ret) => {
        const items = safeArray(ret?.teams || ret?.game_teams || ret || []);
        setAvailableTeams(items);
      })
      .catch(console.error);
  }, [competition_id, myCompanyId]);

  const competition = clubStore.tournament.competition || {};
  const tournamentTeams = safeArray(competition?.teams);
  const divisions = safeArray(clubStore.tournament.divisions || competition?.divisions || []);

  const divisionOptions = useMemo(() => {
    return [
      {label: appStore.tr.no_division || "No Category", value: null},
      ...divisions.map((x) => ({
        label: x.name,
        value: x.id,
      }))
    ];
  }, [divisions]);

  const isOwner =
    Number(competition?.company_id) === Number(myCompanyId) ||
    !!competition?.is_owner;

  const inviteAccepted =
    competition?.invite_status === 'accepted';

  const isInvitedAccepted =
    !!competition?.is_invited && !!inviteAccepted;

  const canAccess = !!competition?.id && (isOwner || isInvitedAccepted);

  const myGameTeamIds = useMemo(() => {
    return new Set(
      safeArray(availableTeams)
        .map((x) => Number(x?.id))
        .filter((x) => Number.isFinite(x))
    );
  }, [availableTeams]);

  const canManageOwnTeam = (item) => {
    const gameTeamId = Number(getTournamentTeamGameTeamId(item));
    return Number.isFinite(gameTeamId) && myGameTeamIds.has(gameTeamId);
  };

  const shownTeams = useMemo(() => {
    return tournamentTeams;
  }, [tournamentTeams]);

  const existingGameTeamIds = useMemo(() => {
    return new Set(
      shownTeams
        .map((x) => Number(getTournamentTeamGameTeamId(x)))
        .filter((x) => Number.isFinite(x))
    );
  }, [shownTeams]);

  const addableTeams = useMemo(() => {
    return safeArray(availableTeams)
      .filter((x) => x?.id)
      .filter((x) => !existingGameTeamIds.has(Number(x.id)))
      .map((x) => ({
        ...x,
        label: x.name,
        value: x.id,
      }));
  }, [availableTeams, existingGameTeamIds]);

  useEffect(() => {
    if (!selectedGameTeamId && addableTeams.length) {
      setSelectedGameTeamId(addableTeams[0].id || addableTeams[0].value);
    }
  }, [addableTeams, selectedGameTeamId]);

  const refreshAfterChange = async () => {
    await clubStore.tournament.getCompetition(competition_id);
    const fresh = await clubStore.tournament.getTeamsForCompany(myCompanyId);
    const items = safeArray(fresh?.teams || fresh?.game_teams || fresh || []);
    setAvailableTeams(items);
  };

  const handleAddTeam = async () => {
    if (!selectedGameTeamId) {
      systemStore.setError(appStore.tr.select_team || "Select team");
      return;
    }

    try {
      setLoadingAdd(true);
      const ret = await clubStore.tournament.addTeamToTournament(
        Number(competition_id),
        Number(selectedGameTeamId)
      );

      if (ret?.result === 0) {
        systemStore.setSuccess(
          appStore.tr.team_added_to_competition || "Team added to competition"
        );
        await refreshAfterChange();
      }
    } catch (err) {
      systemStore.setError(err?.message || "Add failed");
    } finally {
      setLoadingAdd(false);
    }
  };

  const handleRemoveTeam = async (item) => {
    const gameTeamId = getTournamentTeamGameTeamId(item);
    if (!gameTeamId) return;

    if (!window.confirm(appStore.tr.are_you_sure_delete || "Are you sure?")) return;

    try {
      setLoadingRemoveId(gameTeamId);

      const ret = await clubStore.tournament.removeTeamFromTournament(
        Number(competition_id),
        Number(gameTeamId)
      );

      if (ret?.result === 0) {
        systemStore.setSuccess(
          appStore.tr.team_removed_from_competition || "Team removed from competition"
        );
        await refreshAfterChange();
      }
    } catch (err) {
      systemStore.setError(err?.message || "Remove failed");
    } finally {
      setLoadingRemoveId(null);
    }
  };

  const handleDivisionChange = async (item, divisionId) => {
    const tournamentTeamId = getTournamentTeamTournamentTeamId(item);
    if (!tournamentTeamId) return;

    try {
      setSavingDivisionByTournamentTeamId((prev) => ({
        ...prev,
        [tournamentTeamId]: true,
      }));

      const ret = await clubStore.tournament.setTournamentTeamDivision(
        Number(tournamentTeamId),
        divisionId == null ? null : Number(divisionId),
        Number(competition_id)
      );

      if (ret?.result === 0) {
        systemStore.setSuccess(
          appStore.tr.category_assigned || "Category assigned"
        );
        await clubStore.tournament.getCompetition(competition_id);
      }
    } catch (err) {
      systemStore.setError(err?.message || "Save failed");
    } finally {
      setSavingDivisionByTournamentTeamId((prev) => ({
        ...prev,
        [tournamentTeamId]: false,
      }));
    }
  };

  const renderTeamCard = (item) => {
    const gameTeamId = getTournamentTeamGameTeamId(item);
    const tournamentTeamId = getTournamentTeamTournamentTeamId(item);
    const teamName = getTournamentTeamName(item);
    const membersCount = getMembersCount(item);
    const gamesCount = getGamesCount(item);
    const tournamentsCount = getTournamentsCount(item);
    const divisionName = getDivisionName(item);
    const groupName = getGroupName(item);

    const canEditOwnership = canManageOwnTeam(item);
    const canRemove = canManageOwnTeam(item);
    const savingDivision = !!savingDivisionByTournamentTeamId[tournamentTeamId];

    return (
      <div key={tournamentTeamId || gameTeamId} className="col-12 lg:col-6">
        <div className="game-card h-full flex flex-column justify-content-between">
          <div>
            <div className="flex align-items-start gap-3 mb-3">
              <span className="badge badge-upcoming flex-shrink-0">
                <i className="pi pi-users" aria-hidden="true" />
              </span>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-lg line-height-3" title={teamName}>{teamName}</div>
                <div className="text-600 text-sm mt-1">
                  <i className="pi pi-sitemap mr-2" aria-hidden="true" />
                  {appStore.tr.groups}: {groupName}
                </div>
              </div>
            </div>

            {canEditOwnership ? (
              <div className="mb-3">
                <label htmlFor={`division_${tournamentTeamId}`} className="block mb-2 text-600 text-sm font-semibold">
                  {appStore.tr.division || "Category"}
                </label>
                <Select
                  id={`division_${tournamentTeamId}`}
                  value={item?.division_id ?? item?.division?.id ?? null}
                  options={divisionOptions}
                  optionLabel="label"
                  optionValue="value"
                  disabled={savingDivision || systemStore.loading}
                  onChange={(val) => handleDivisionChange(item, val)}
                />
              </div>
            ) : (
              <div className="mb-3 text-600 text-sm">
                <i className="pi pi-tag mr-2" aria-hidden="true" />
                {appStore.tr.division}: <span className="font-semibold">{divisionName}</span>
              </div>
            )}

            <div className="flex flex-column gap-2 mt-3">
              <div className="competition-detail-item flex align-items-center justify-content-between gap-2">
                <span className="text-600 text-sm">
                  <i className="pi pi-users mr-2" aria-hidden="true" />
                  {appStore.tr.players || "Players"}
                </span>
                <span className="text-lg font-bold flex-shrink-0">{membersCount}</span>
              </div>
              <div className="competition-detail-item flex align-items-center justify-content-between gap-2">
                <span className="text-600 text-sm">
                  <i className="pi pi-calendar mr-2" aria-hidden="true" />
                  {appStore.tr.games}
                </span>
                <span className="text-lg font-bold flex-shrink-0">{gamesCount}</span>
              </div>
              <div className="competition-detail-item flex align-items-center justify-content-between gap-2">
                <span className="text-600 text-sm">
                  <i className="pi pi-trophy mr-2" aria-hidden="true" />
                  {appStore.tr.tournaments || "Tournaments"}
                </span>
                <span className="text-lg font-bold flex-shrink-0">{tournamentsCount}</span>
              </div>
            </div>
          </div>

          <div className="game-card-actions flex flex-wrap gap-2 mt-3">
            <Btn
              icon="pi pi-pencil"
              label={appStore.tr.edit}
              outlined
              onClick={() => navigate(`${basePath}/games/game_team/${gameTeamId}`)}
            />
            {canRemove && (
              <Btn
                icon="pi pi-trash"
                label={appStore.tr.delete}
                severity="danger"
                outlined
                loading={loadingRemoveId === gameTeamId}
                onClick={() => handleRemoveTeam(item)}
              />
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <PageLayout
      header={
        <div className="flex align-items-center gap-3 w-full">
          <i className="pi pi-users text-primary text-xl" aria-hidden="true" />
          <div className="flex flex-column gap-1 min-w-0">
            <h3 className="m-0">{appStore.tr.competition_teams}</h3>
            <span className="text-600 text-sm">{competition.name}</span>
          </div>
        </div>
      }
    >
      <div className="col-12">
        {!canAccess ? (
          <div className="game-page p-4 text-center">
            <i className="pi pi-lock text-600 text-xl mb-2" aria-hidden="true" />
            <div className="text-600">{appStore.tr.access_denied || "Access denied"}</div>
          </div>
        ) : (
          <>
            <div className="game-page p-3 mb-3">
              <div className="flex align-items-center gap-2 mb-3">
                <i className="pi pi-plus-circle text-primary" aria-hidden="true" />
                <span className="font-semibold">{appStore.tr.add_team}</span>
              </div>
              <div className="grid align-items-end">
                <div className="col-12 md:col-8">
                  <label htmlFor="competition_team_select" className="block mb-2 text-600 text-sm font-semibold">
                    {appStore.tr.select_team}
                  </label>
                  <Select
                    id="competition_team_select"
                    value={selectedGameTeamId}
                    options={addableTeams}
                    optionLabel="label"
                    optionValue="value"
                    placeholder={appStore.tr.select_team}
                    onChange={(val) => setSelectedGameTeamId(val)}
                    disabled={!addableTeams.length || systemStore.loading}
                  />
                </div>
                <div className="col-12 md:col-4">
                  <Btn
                    icon="pi pi-plus"
                    label={appStore.tr.add_team}
                    severity="success"
                    className="w-full"
                    loading={loadingAdd}
                    disabled={!selectedGameTeamId || !addableTeams.length}
                    onClick={handleAddTeam}
                  />
                </div>
              </div>
              {!addableTeams.length && (
                <div className="text-600 text-sm mt-2">
                  {appStore.tr.no_teams_available || "No teams available"}
                </div>
              )}
            </div>

            <div className="flex align-items-center justify-content-between gap-2 mb-3">
              <h4 className="m-0">{appStore.tr.competition_teams}</h4>
              <span className="badge badge-upcoming">{shownTeams.length}</span>
            </div>
            {!shownTeams.length ? (
              <div className="game-page p-4 text-center">
                <i className="pi pi-users text-600 text-xl mb-2" aria-hidden="true" />
                <div className="text-600">
                  {appStore.tr.no_competition_teams || "No competition teams"}
                </div>
              </div>
            ) : (
              <div className="grid">
                {shownTeams.map(renderTeamCard)}
              </div>
            )}
          </>
        )}
      </div>
    </PageLayout>
  );
};

export default observer(CompetitionTeams);
